import { Loader } from '@assets/icons'
import { HeaderPage, InputForm, LinkButton, Paginator, PopUpGeneric, TableGeneric } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IColumn, IListPaginated } from '@interfaces/index'
import { IListTableSales, ISearchTableSalesFilters } from '@interfaces/tables/tableSales'
import { errorHandler } from '@services/index'
import TableSalesService from '@services/requests/tableSalesService'
import { IParamsUrl, getQueryParamsInLine } from '@utils/getQueryParams/getQueryParams'
import { downloadPdf, getQueryParams, totalPages } from '@utils/index'
import React from 'react'
import { confirmAlert } from 'react-confirm-alert'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineSearch } from 'react-icons/ai'
import { FiDownload } from 'react-icons/fi'
import { HiOutlineClipboardCopy } from 'react-icons/hi'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'

export default function ListTableSales(): React.ReactElement {
    const navigate = useNavigate()
    const paramsUrl = getQueryParams()

    const [data, setData] = React.useState<IListPaginated<IListTableSales>>(defaultPagination)
    const [isLoading, setIsLoading] = React.useState(false)

    const { handleSubmit, control, reset } = useForm<ISearchTableSalesFilters>()

    function init(paramsUrl?: IParamsUrl): void {
        if (paramsUrl) {
            reset(paramsUrl)
            getList(paramsUrl)
        }
    }

    React.useEffect(() => {
        init(paramsUrl)
    }, [paramsUrl])

    async function getList(paramsUrl?: IParamsUrl): Promise<void> {
        setIsLoading(true)
        try {
            const response = await TableSalesService.list(paramsUrl)
            if (response?.page !== 1 && response.items.length <= 0) {
                navigate(`?page=${response?.page - 1}`)
            } else {
                setData(response)
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function popUpConfirm(id: number): void {
        confirmAlert({
            customUI: ({ onClose }) => (
                <PopUpGeneric
                    onConfirm={() => {
                        deleteSaleTable(id)
                        onClose()
                    }}
                    onCancel={onClose}
                    title={'Deseja excluir esta Tabela de vendas?'}
                />
            ),
        })
    }

    async function deleteSaleTable(id: number): Promise<void> {
        setIsLoading(true)
        try {
            const response = await TableSalesService.delete(id)
            toast(response.message, { type: 'success' })
            init(getQueryParamsInLine())
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function submit(values: ISearchTableSalesFilters): void {
        navigate(
            {
                pathname: '/tabela-de-vendas',
                search: createSearchParams({
                    ...values,
                    page: '1'
                }).toString()
            }
        )
    }

    const columns: IColumn<IListTableSales>[] = React.useMemo<IColumn<IListTableSales>[]>(
        () => [
            {
                header: 'Nome',
                dataIndex: 'name',
            },
            {
                header: 'Data da Tabela',
                dataIndex: 'tableDate',
                render: (item) => {
                    const arrayDate = item.tableDate.split('-')
                    const month = arrayDate[1]
                    const year = arrayDate[0]
                    return (
                        <div>{month}/{year}</div>
                    )
                },
                size: '12rem'
            },
            {
                header: 'Imóvel',
                dataIndex: 'realEstate',
                render: (item) => (
                    item.realEstate.name
                )
            },
            {
                header: 'Download',
                dataIndex: 'download',
                align: 'align-center',
                render: (item) => {
                    return item.hasTemplate ? (
                        <div>
                            <button className='button-download' onClick={() => {
                                const siteUrl = process.env.REACT_APP_SITE_URL as string
                                navigator.clipboard.writeText(`${siteUrl}/tabela-vendas/${item.id}`)
                                toast('Link copiado!', { type: 'success' })
                            }}>
                                <HiOutlineClipboardCopy size={20} data-tooltip-id='tooltip-copy' data-tooltip-content="Copiar" />
                            </button>
                            <ButtonDownload {...item} />
                            <Tooltip
                                id='tooltip-download'
                                place="bottom"
                                opacity={100}
                                className="visualized-tooltip" />
                            <Tooltip
                                id='tooltip-copy'
                                place="bottom"
                                opacity={100}
                                className="visualized-tooltip" />
                        </div>
                    ) : (
                        <></>
                    )
                },
            },
            {
                header: 'Ação',
                dataIndex: 'actions',
                align: 'align-center',
                size: '8rem',
                render: (item) => (
                    <div className='col-actions'>
                        <LinkButton title='Editar' path={`/tabela-de-vendas/editar/${item.id}`} />
                        <button
                            className='link-button'
                            title='Excluir'
                            onClick={() => popUpConfirm(item.id)}
                        >
                            Excluir
                        </button>
                    </div>
                ),
            },
        ],
        []
    )

    return (
        <div className='card'>
            <div className='list-table-sales'>
                <HeaderPage title="Tabela de Vendas" textButton="Nova Tabela" onClickButton={() => navigate('/tabela-de-vendas/cadastrar')} />
                <div>
                    <form className='table-sales-filters' onSubmit={handleSubmit(submit)}>
                        <Controller
                            control={control}
                            name='realEstateName'
                            defaultValue=''
                            render={({ field: { value, onChange } }) => (
                                <InputForm
                                    size="small"
                                    value={value}
                                    onChange={onChange}
                                    onInputCallBack={() => {
                                        handleSubmit(submit)()
                                    }}
                                    placeholder="Buscar por nome ou imóvel"
                                    style={{ width: '23rem' }}
                                    InputProps={{
                                        endAdornment: (
                                            <button type='submit' className='button-search'>
                                                <AiOutlineSearch />
                                            </button>
                                        )
                                    }}
                                />
                            )}
                        />
                    </form>
                    <div>
                        <TableGeneric data={data.items} columns={columns} isLoading={isLoading} />
                        <div className="paginator">
                            <Paginator
                                maxElements={7}
                                current={data.page}
                                total={totalPages(data.count, data.perPage)}
                                onClick={(page) => navigate(
                                    {
                                        pathname: '/tabela-de-vendas',
                                        search: createSearchParams({
                                            ...paramsUrl,
                                            page: page.toString(),
                                        }).toString()
                                    }
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function ButtonDownload(item: IListTableSales): React.ReactElement {
    const [isLoadingDownload, setIsLoadingDownload] = React.useState(false)
    return (
        <button
            className='button-download'
            onClick={async () => {
                setIsLoadingDownload(true)
                const response = await TableSalesService.getPdfLink(item.id)
                await downloadPdf(response.fileFromS3, item.name)
                setIsLoadingDownload(false)
            }}>
            {isLoadingDownload ?
                <Loader style={{ width: '1rem', fill: '#707070' }} />
                :
                <FiDownload size={20} data-tooltip-id='tooltip-download' data-tooltip-content="Download" />
            }
        </button>
    )
}